@if (highlightBox) {
  <div
    class="spotlight-overlay"
    (click)="onOverlayClick()"
    (keydown.enter)="onOverlayClick()"
    (keydown.escape)="onOverlayClick()"
    role="dialog"
    [attr.aria-label]="ariaLabel"
    [attr.aria-describedby]="annotated ? regionListId : null"
    tabindex="0"
    [style.left.px]="canvasOffsetX"
    [style.top.px]="canvasOffsetY"
    [style.width.px]="canvasWidth"
    [style.height.px]="canvasHeight"
  >
    <!-- One gray rectangle with a mask that cuts a window for every highlighted region -->
    <svg #spotlightSvg [attr.width]="canvasWidth" [attr.height]="canvasHeight" class="spotlight-svg" aria-hidden="true">
      <defs>
        <!-- Mask: white everywhere except the windows (black) -->
        <mask [attr.id]="maskId">
          <!-- White = visible, black = transparent -->
          <rect width="100%" height="100%" fill="white" />
          <!-- One window per region (Issue #2254), each with its own gradient border for glow -->
          <!-- Expand by the box-proportional feather to extend the blur zone (Issue #1496) -->
          <!-- Windows are tracked by their box, so only one that appears or disappears fades (Issue #3127) -->
          @for (cutout of cutouts; track cutout.key) {
            <!-- data-cutout is the window without its feather, in pixels of the displayed canvas. -->
            <rect
              data-testid="preview-spotlight-cutout"
              class="spotlight-window"
              [class.spotlight-window-dimmed]="cutout.state === 'dimmed'"
              [class.spotlight-window-leaving]="cutout.state === 'leaving'"
              [class.spotlight-window-entering]="cutout.entering"
              [attr.data-window-state]="cutout.state"
              [attr.data-cutout]="cutout.x + ',' + cutout.y + ',' + cutout.width + ',' + cutout.height"
              [attr.x]="cutout.x - cutout.feather"
              [attr.y]="cutout.y - cutout.feather"
              [attr.width]="cutout.width + cutout.feather * 2"
              [attr.height]="cutout.height + cutout.feather * 2"
              fill="black"
              [attr.filter]="cutout.feather > 0 ? 'url(#' + cutout.filterId + ')' : null"
            />
          }
        </mask>
        <!-- Blur filters for the soft gradient edges: one per window, sized to that window -->
        @for (cutout of cutouts; track $index) {
          <filter [attr.id]="cutout.filterId" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur [attr.stdDeviation]="cutout.feather / 5" />
          </filter>
        }
      </defs>
      <!-- Single gray overlay with mask applied -->
      <rect
        width="100%"
        height="100%"
        [attr.fill]="'rgba(0, 0, 0, ' + overlayOpacity + ')'"
        [attr.mask]="'url(#' + maskId + ')'"
      />
      <!-- Outlines and labels: only for several regions, or a labelled one (Issue #2254) -->
      @if (annotated) {
        @for (cutout of cutouts; track cutout.key) {
          @if (cutout.state === 'shown') {
            <rect
              class="spotlight-outline-shadow"
              [attr.x]="cutout.x - outlineOutset"
              [attr.y]="cutout.y - outlineOutset"
              [attr.width]="cutout.width + outlineOutset * 2"
              [attr.height]="cutout.height + outlineOutset * 2"
            />
            <rect
              class="spotlight-outline"
              [attr.x]="cutout.x - outlineOutset"
              [attr.y]="cutout.y - outlineOutset"
              [attr.width]="cutout.width + outlineOutset * 2"
              [attr.height]="cutout.height + outlineOutset * 2"
            />
          }
        }
        @for (label of labels; track $index) {
          <g class="spotlight-label">
            <rect [attr.x]="label.x" [attr.y]="label.y" [attr.width]="label.width" [attr.height]="label.height" />
            <text [attr.x]="label.x + labelPaddingX" [attr.y]="label.y + label.height / 2">{{ label.text }}</text>
          </g>
        }
      }
    </svg>
    <!-- The same regions as text, for screen readers: the SVG above is a picture -->
    @if (annotated) {
      <ul class="spotlight-region-list" [attr.id]="regionListId">
        @for (description of regionDescriptions; track $index) {
          <li>{{ description }}</li>
        }
      </ul>
    }
    <!-- Dismiss hint -->
    <div class="dismiss-hint">
      <span>
        Press
        <kbd>ESC</kbd>
        or click to dismiss
      </span>
    </div>
  </div>
}
